<script setup>
/**
 * Overview: "is my AI healthy", one screen. THE PLAN, §4 P4; ease package P-5.1, P-5.2.
 *
 * Four blind critics shaped this page: a SENTENCE first (what it is doing right now), then
 * the two or three numbers a shop owner wants, then a ranked queue where every row carries a
 * verb, and the reply switch small because the state beside it is large.
 *
 * THE SENTENCE IS THE SERVER'S. It used to be ranked here from three reads (credits, limits,
 * the switch) while the channel rows read a fourth, so an account out of credits was
 * "stopped" in the headline and "Answering" on every channel. `GET …/status` ranks once
 * (`ai-sidecar/src/status/compute.ts`, hardest stop first) and the headline, every channel
 * row, the in-chat panel and the Test it header all say that one answer. A status this page
 * cannot read is said as such ("We couldn't check your assistant"), never as "answering".
 *
 * PRACTICE (P-5.2). A new account answers only its test numbers. While it practises the
 * readiness path stands where the queue's calm all-clear would be; "Start answering
 * customers" in the headline opens its confirm. Once live, "Back to practice" sits under the
 * headline, behind a confirm that says what it means.
 *
 * NOTHING ON THIS PAGE IS DERIVED FROM AN ABSENCE. `config` is the only read the page cannot
 * survive (without it there is no switch). Every other region degrades in place and says
 * what it could not read; the queue names a source it could not check rather than
 * contributing zero rows. `limits` degrades SILENTLY because all it adds is a warning.
 */
import { computed, ref } from 'vue';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import AiSwitch from '../components/AiSwitch.vue';
import AssistantStatus from '../components/AssistantStatus.vue';
import AttentionQueue from '../components/AttentionQueue.vue';
import CreditBalanceCard from '../components/CreditBalanceCard.vue';
import ChannelRow from '../components/ChannelRow.vue';
import ReadinessPath from '../components/ReadinessPath.vue';
import TestItButton from '../components/TestItButton.vue';
import StatTile from '../components/StatTile.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import WeekSummaryCard from '../components/WeekSummaryCard.vue';
import WhatChangedCard from '../components/WhatChangedCard.vue';
import ClaudeBatches from '../components/ClaudeBatches.vue';
import ConnectedAppsCard from '../components/ConnectedAppsCard.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { useWasloStatus } from '../composables/useWasloStatus.js';
import { buildAttentionQueue } from '../helpers/attention.js';
import { toIntlLocale } from '../helpers/intl.js';
import { relativeTime } from '../helpers/relative-time.js';
import { wasloApi } from '../api/client.js';

const { t, locale } = useWasloI18n();

const accountId = useWasloAccountId();

const config = useWasloApi(signal =>
  wasloApi.getConfig(accountId.value, signal)
);
/** THE one status. See the header. */
const status = useWasloStatus(accountId);
const credits = useWasloApi(signal =>
  wasloApi.getCredits(accountId.value, signal)
);
const activity = useWasloApi(signal =>
  wasloApi.getActivity(accountId.value, signal)
);
/** P-5.4: the last 7 whole days, and her own weekly-email setting. Each fails on its own. */
const week = useWasloApi(signal => wasloApi.getWeek(accountId.value, signal));
const weekEmail = useWasloApi(signal =>
  wasloApi.getWeekEmail(accountId.value, signal)
);
// `isEmpty: () => false` on the list reads: an empty list is a MEANING here ("no channel
// is set up yet"), not the page's empty state.
const inboxes = useWasloApi(
  signal => wasloApi.getInboxes(accountId.value, signal),
  { isEmpty: () => false }
);
/** P-3.5: each connection's week of live use, so one that keeps failing becomes a row. */
const connections = useWasloApi(
  signal => wasloApi.getConnections(accountId.value, signal),
  { isEmpty: () => false }
);
/** How much of this month's limit is gone. A warning only, so it degrades silently. */
const limits = useWasloApi(signal =>
  wasloApi.getLimits(accountId.value, signal)
);

/**
 * P-4.6: WhatsApp's verdicts on her approved messages. A read that fails is named in the queue
 * ("we couldn't check what WhatsApp decided"), never shown as nothing to do.
 */
const templateEvents = useWasloApi(
  signal => wasloApi.getTemplateEvents(accountId.value, signal),
  { isEmpty: () => false }
);

/** P-10.21: tasks that are on and use a switched-off connection, for the queue. */
const connectionUse = useWasloApi(
  signal => wasloApi.getConnectionUse(accountId.value, signal),
  { isEmpty: () => false }
);

/**
 * P-1.7: what the team suggested, for a count in the queue. NOT in `regions`: it is a to-do,
 * and a failed read is simply no row, never a page that waits or says "not checked".
 */
const suggestions = useWasloApi(
  signal => wasloApi.getTeamSuggestions(accountId.value, signal),
  { isEmpty: () => false }
);
/** C3 P-6.9: the "For now" notes, for the "ends tomorrow" row. A to-do like P-1.7. */
const notices = useWasloApi(
  signal => wasloApi.getNotices(accountId.value, signal),
  { isEmpty: () => false }
);

const regions = [
  config,
  status,
  credits,
  activity,
  week,
  weekEmail,
  inboxes,
  limits,
  connections,
  connectionUse,
  templateEvents,
];

// Only `config` can fail the page. See the header.
const errorMessage = computed(() => config.errorMessage.value);

// `isFetching` counts IDLE as fetching, which is what stops a fabricated "0 credits / Off"
// reaching the first frame. A region that fails leaves LOADING for ERROR, so none can hang it.
const isFetching = computed(
  () => !errorMessage.value && regions.some(region => region.isFetching.value)
);

const reload = () => regions.forEach(region => region.reload());

const aiEnabled = computed(() => config.data.value?.aiEnabled === true);

/**
 * The legacy tri-state for a channel row whose BFF sent no `state` (one version behind).
 * A row that has a `state` says it and ignores this.
 */
const repliesState = computed(() => {
  if (config.error.value || !config.data.value) return 'unknown';
  return config.data.value.aiEnabled ? 'on' : 'off';
});

const statusData = computed(() => status.data.value);
const accountStatus = computed(() => status.account.value);
const isPractising = computed(() => status.isPractising.value);
const isLive = computed(() => accountStatus.value?.mode === 'live');

const creditsData = computed(() => credits.data.value);
const activityData = computed(() => activity.data.value);
const inboxesData = computed(() => inboxes.data.value);
const connectionsData = computed(() => connections.data.value);
const limitsData = computed(() => limits.data.value);

// vue-i18n hands us Chatwoot's directory name (`pt_BR`), which `Intl` rejects outright.
const intlLocale = computed(() => toIntlLocale(locale.value));
const formatCount = value =>
  new Intl.NumberFormat(intlLocale.value).format(value);

/** A whole 0 to 100, clamped: a server that reports 250 is wrong about something. */
const formatPercent = whole =>
  new Intl.NumberFormat(intlLocale.value, {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(Math.max(0, Math.min(100, whole)) / 100);

// ── the status line ────────────────────────────────────────────────────────────────
/**
 * The second line while it is answering: when it last said anything, which is the one fact
 * that proves the whole thing is alive. Every other state says its own meaning instead.
 */
const statusDetail = computed(() => {
  if (accountStatus.value?.state !== 'answering') return '';
  const data = activityData.value;
  if (!data) return t('WASLO.OVERVIEW.STATUS.UNKNOWN_BODY');
  const when = relativeTime(data.lastReplyAt, locale.value, {
    justNow: t('WASLO.OVERVIEW.STATUS.JUST_NOW'),
  });
  return when
    ? t('WASLO.OVERVIEW.STATUS.LAST_REPLY', { when })
    : t('WASLO.OVERVIEW.STATUS.NO_REPLY', { days: data.window.days });
});

/** Shown only from halfway, and only ever a percentage: no money reaches this page. */
const USAGE_VISIBLE_FROM = 50;

const monthUsageLine = computed(() => {
  const used = limitsData.value?.month?.usedPercent;
  if (typeof used !== 'number' || used < USAGE_VISIBLE_FROM) return '';
  return t('WASLO.OVERVIEW.LIMITS.MONTH_USED', {
    percent: formatPercent(used),
  });
});

const liveSinceLine = computed(() => {
  const at = statusData.value?.readiness?.liveSince;
  if (!isLive.value || typeof at !== 'string') return '';
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return '';
  return t('WASLO.READINESS.LIVE_SINCE', {
    date: new Intl.DateTimeFormat(intlLocale.value, {
      dateStyle: 'medium',
    }).format(date),
  });
});

/** Render from the response, never from what we sent; the status is read again, because
 *  turning replies on or off changes the sentence at the top of this page. */
const onConfigSaved = next => {
  config.data.value = next;
  status.reload();
};

// ── practice ───────────────────────────────────────────────────────────────────────
const readinessRef = ref(null);
const onGoLive = () => readinessRef.value?.askGoLive();
/** Every write on the readiness path answers with the whole new status. */
const onStatusChanged = next => {
  status.data.value = next;
};

/** P-5.4: render her saved choice, never the click. */
const onWeekEmailSaved = next => {
  weekEmail.data.value = next;
};

const backConfirming = ref(false);
const backBusy = ref(false);
const backError = ref('');

const askBack = () => {
  backError.value = '';
  backConfirming.value = true;
};

const goBack = async () => {
  if (backBusy.value || accountId.value === null) return;
  backBusy.value = true;
  backError.value = '';
  try {
    status.data.value = await wasloApi.backToPractice(accountId.value);
    backConfirming.value = false;
  } catch {
    backError.value = t('WASLO.READINESS.BACK_FAILED');
  } finally {
    backBusy.value = false;
  }
};

// ── the numbers ────────────────────────────────────────────────────────────────────
const dayTitle = computed(() =>
  activityData.value
    ? t('WASLO.OVERVIEW.NUMBERS.DAY_TITLE', {
        day: activityData.value.today.day,
      })
    : ''
);

const windowHint = count =>
  activityData.value
    ? t(
        'WASLO.OVERVIEW.NUMBERS.WINDOW_HINT',
        {
          count: formatCount(count),
          days: activityData.value.window.days,
        },
        // The Arabic noun follows the days, not the count (P-7.3).
        activityData.value.window.days
      )
    : '';

/** Urgency decides the layout: a low balance takes the whole row, above the counters. */
const creditsUrgent = computed(() => creditsData.value?.lowBalance === true);

const numbersGrid = computed(() => {
  if (!activityData.value) return 'sm:grid-cols-1';
  return creditsUrgent.value ? 'sm:grid-cols-2' : 'sm:grid-cols-3';
});

/** A row's fix changed something: the handover setting (P-1.3) or a fixed question (C3). */
const onQueueChanged = () => {
  config.reload();
  activity.reload();
};

// ── the queue ──────────────────────────────────────────────────────────────────────
// Not `credits`: the balance has one owner on this page, the card above.
const attention = computed(() =>
  buildAttentionQueue({
    inboxes: inboxesData.value,
    needsYou: statusData.value?.needsYou ?? null,
    activity: activityData.value,
    connections: connectionsData.value,
    connectionUse: connectionUse.data.value,
    templateEvents: templateEvents.data.value,
    // P-1.3: unknown from a BFF older than the setting (`handover` absent), so no row.
    handover:
      config.data.value && config.data.value.handover !== undefined
        ? { set: config.data.value.handover !== null }
        : null,
    suggestions: suggestions.data.value,
    notices: notices.data.value ?? null,
  })
);
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.OVERVIEW.TITLE')"
    :is-fetching="isFetching"
    :not-provisioned="config.isNotProvisioned.value"
    :error-message="errorMessage"
    @retry="reload"
  >
    <template #action>
      <TestItButton />
    </template>

    <template #body>
      <div class="flex flex-col gap-8 pb-10">
        <!-- 1 · WHAT IT IS DOING RIGHT NOW, in the server's one sentence. -->
        <section class="flex flex-col gap-3" data-waslo-status>
          <div
            class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
          >
            <AssistantStatus
              :status="accountStatus"
              :account-id="accountId"
              :detail="statusDetail"
              :top-up-told="creditsData?.alreadyTold === true"
              go-live-here
              @go-live="onGoLive"
              @changed="onConfigSaved"
            >
              <p
                v-if="monthUsageLine"
                class="mb-0 text-sm text-n-slate-11"
                data-waslo-limit-usage
              >
                {{ monthUsageLine }}
              </p>
              <p
                v-if="liveSinceLine"
                class="mb-0 text-sm text-n-slate-11"
                data-waslo-live-since
              >
                {{ liveSinceLine }}
              </p>
              <div
                v-if="isLive"
                class="flex flex-wrap items-center gap-2 pt-1"
                aria-live="polite"
              >
                <Button
                  v-if="!backConfirming"
                  :label="t('WASLO.READINESS.BACK')"
                  variant="link"
                  color="slate"
                  size="sm"
                  data-waslo-back-to-practice
                  @click="askBack"
                />
                <template v-else>
                  <p
                    class="w-full mb-0 text-sm text-n-slate-12"
                    data-waslo-back-confirm
                  >
                    {{ t('WASLO.READINESS.BACK_CONFIRM') }}
                  </p>
                  <Button
                    :label="t('WASLO.READINESS.BACK_YES')"
                    size="sm"
                    variant="faded"
                    color="slate"
                    :is-loading="backBusy"
                    data-waslo-back-yes
                    @click="goBack"
                  />
                  <Button
                    :label="t('WASLO.READINESS.BACK_NO')"
                    size="sm"
                    variant="ghost"
                    color="slate"
                    data-waslo-back-no
                    @click="backConfirming = false"
                  />
                </template>
                <p
                  v-if="backError"
                  class="w-full mb-0 text-sm text-n-ruby-11"
                  data-waslo-back-error
                >
                  {{ backError }}
                </p>
              </div>
            </AssistantStatus>

            <!-- The control is small BECAUSE the state is large. -->
            <AiSwitch
              class="shrink-0"
              :model-value="aiEnabled"
              :account-id="accountId"
              @saved="onConfigSaved"
            />
          </div>
        </section>

        <!-- 1b · PRACTICE: the way to answering every customer (P-5.2). -->
        <section v-if="isPractising && statusData" class="flex flex-col gap-4">
          <ReadinessPath
            ref="readinessRef"
            :status="statusData"
            :account-id="accountId"
            @changed="onStatusChanged"
          />
        </section>

        <!-- 2 · THE NUMBERS. Credits takes the whole row the moment it is urgent. -->
        <section class="flex flex-col gap-4" data-waslo-numbers>
          <CreditBalanceCard
            v-if="creditsUrgent"
            :credits="creditsData"
            :account-id="accountId"
          />

          <div class="grid gap-4" :class="numbersGrid">
            <template v-if="activityData">
              <StatTile
                :label="t('WASLO.OVERVIEW.NUMBERS.CONVERSATIONS')"
                :value="formatCount(activityData.today.conversations)"
                :hint="windowHint(activityData.window.conversations)"
                :day-title="dayTitle"
                data-waslo-stat-conversations
              />
              <StatTile
                :label="t('WASLO.OVERVIEW.NUMBERS.HANDOFFS')"
                :value="formatCount(activityData.today.handoffConversations)"
                :hint="windowHint(activityData.window.handoffConversations)"
                :day-title="dayTitle"
                data-waslo-stat-handoffs
              />
            </template>

            <CreditBalanceCard
              v-if="!creditsUrgent"
              :credits="creditsData"
              :account-id="accountId"
            />
          </div>

          <Banner
            v-if="!activityData"
            color="slate"
            data-waslo-numbers-unavailable
          >
            {{ t('WASLO.OVERVIEW.NUMBERS.UNAVAILABLE') }}
          </Banner>
        </section>

        <!-- 2b · THIS WEEK (P-5.4): the last 7 whole days, in her own days. -->
        <section class="flex flex-col gap-4" data-waslo-week-section>
          <WeekSummaryCard
            :week="week.data.value"
            :email="weekEmail.data.value"
            :account-id="accountId"
            @email-saved="onWeekEmailSaved"
          />
        </section>

        <!-- 2c · SOMETHING CHANGED (P-8.3): one sentence, every page it touches, one Undo. -->
        <section class="flex flex-col gap-4" data-waslo-what-changed-section>
          <WhatChangedCard :account-id="accountId" />
        </section>

        <!-- 2d · CLAUDE (P-8.4): what her own Claude drafted, Undo all, and the apps she connected. -->
        <section class="flex flex-col gap-4" data-waslo-claude-section>
          <ClaudeBatches :account-id="accountId" />
          <ConnectedAppsCard :account-id="accountId" />
        </section>

        <!-- 3 · WHAT TO DO ABOUT IT. -->
        <section class="flex flex-col gap-4">
          <SettingsHeader
            :heading="t('WASLO.OVERVIEW.ATTENTION.HEADING')"
            :description="t('WASLO.OVERVIEW.ATTENTION.DESCRIPTION')"
          />
          <AttentionQueue
            :rows="attention.rows"
            :unchecked="attention.unchecked"
            :account-id="accountId"
            :practising="isPractising"
            @changed="onQueueChanged"
          />
        </section>

        <!-- 4 · WHERE IT IS RUNNING: every channel, not only WhatsApp (P-5.5). -->
        <section class="flex flex-col gap-4" data-waslo-channels>
          <SettingsHeader
            :heading="t('WASLO.OVERVIEW.CHANNELS.HEADING')"
            :description="t('WASLO.OVERVIEW.CHANNELS.DESCRIPTION')"
          />

          <Banner v-if="!inboxesData" color="slate">
            {{ t('WASLO.OVERVIEW.CHANNELS.UNAVAILABLE') }}
          </Banner>
          <ul
            v-else-if="inboxesData.length"
            class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
            data-waslo-channel-list
          >
            <ChannelRow
              v-for="inbox in inboxesData"
              :key="inbox.id"
              :inbox="inbox"
              :replies="repliesState"
            />
          </ul>
          <p v-else class="mb-0 text-sm text-n-slate-11">
            {{ t('WASLO.OVERVIEW.CHANNELS.NONE') }}
          </p>
        </section>
      </div>
    </template>
  </WasloPageLayout>
</template>
